import { createFileRoute } from '@tanstack/react-router'
import { AssessmentDetails } from '@/features/assessments'
import { useConvexAssessment } from '@/features/assessments/services/assessments.convex'
import type { DetailedAssessment } from '@/features/assessments/types'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/(dashboard)/assessments/$assessmentId/')(
  {
    component: RouteComponent,
    beforeLoad: ({ location }) => {
      // Check if we came from DirectReports route by looking at the state
      const isFromDirectReports =
        (location.state as { from?: string } | undefined)?.from ===
        'Direct Reports'

      return {
        breadcrumbs: isFromDirectReports
          ? [
              { title: 'Assessments', href: `/assessments` },
              { title: 'Direct Reports', href: `/assessments/direct-reports` },
              { title: `Overview` },
            ]
          : [
              { title: 'Assessments', href: `/assessments` },
              { title: `Overview` },
            ],
      }
    },
  },
)

function RouteComponent() {
  const { assessmentId } = Route.useParams()

  const raw = useConvexAssessment(assessmentId)

  // Adapt Convex shape → DetailedAssessment (field name aliases)
  const assessment: DetailedAssessment | null | undefined = raw
    ? ({
        id: raw._id,
        employeeId: raw.employeeUserId,
        managerId: raw.managerUserId,
        organizationId: raw.organizationId,
        employee: {
          id: raw.employee._id,
          name: raw.employee.name ?? '',
          email: raw.employee.email ?? '',
        },
        manager: {
          id: raw.manager._id,
          name: raw.manager.name ?? '',
          email: raw.manager.email ?? '',
        },
        assessmentType: raw.assessmentType
          ? {
              id: raw.assessmentType._id,
              name: raw.assessmentType.name,
              positionId: raw.assessmentType.positionId,
              position: raw.assessmentType.position
                ? {
                    id: raw.assessmentType.position._id,
                    title: raw.assessmentType.position.title,
                    level: raw.assessmentType.position.level,
                    role: raw.assessmentType.position.role,
                  }
                : undefined,
            }
          : undefined,
        responses: raw.responses.map((r) => ({
          id: r._id,
          userId: r.userId,
          responseType: r.responseType,
          completedAt: r.completedAt
            ? new Date(r.completedAt).toISOString()
            : null,
          comment: r.comment ?? null,
        })),
        createdAt: raw.createdAt
          ? new Date(raw.createdAt).toISOString()
          : undefined,
      } as DetailedAssessment)
    : raw

  // Convex useQuery returns undefined while loading, null if not found
  const isLoading = raw === undefined

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Row 1: Assessment Info and Score Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Assessment Type Card */}
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0 space-y-4">
              <div>
                <Skeleton className="h-3 w-32 mb-2" />
                <Skeleton className="h-6 w-40" />
              </div>
              <div className="space-y-2 pt-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-4 w-24" />
              </div>
            </CardContent>
          </Card>

          {/* Assessment Details Card */}
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0 space-y-4">
              <div>
                <Skeleton className="h-3 w-36 mb-2" />
                <Skeleton className="h-6 w-40" />
              </div>
              <div className="space-y-2 pt-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-28" />
              </div>
            </CardContent>
          </Card>

          {/* Manager Score Card */}
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-3 w-28 mb-3" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>

          {/* DirectReport Score Card */}
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-3 w-32 mb-3" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
        </div>

        {/* Tabs Skeleton */}
        <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit">
          <div className="h-8 w-28 rounded-md bg-background/60 animate-pulse" />
          <div className="h-8 w-28 rounded-md bg-background/60 animate-pulse ml-1" />
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Feedback Radar Chart */}
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-5 w-40 mb-6" />
              <div className="flex items-center justify-center py-12">
                <Skeleton className="w-64 h-64" />
              </div>
            </CardContent>
          </Card>

          {/* Summary Average */}
          <Card className="p-6 bg-muted/30 rounded-lg border-none shadow-none">
            <CardContent className="p-0">
              <Skeleton className="h-5 w-36 mb-6" />
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 rounded-md overflow-hidden">
                    <Skeleton className="h-full w-full" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!assessment) {
    return (
      <Card>
        <CardContent className="p-6">
          <p>Assessment not found</p>
        </CardContent>
      </Card>
    )
  }

  return <AssessmentDetails assessment={assessment} />
}
